import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { RegistrationSuccessAnimation } from '@/components/animations/RegistrationSuccessAnimation';
import { consultarCPF as apiConsultarCPF, consultarCNPJ as apiConsultarCNPJ, startRegistration, sendOtp, validateOtp, finalizeRegistration } from '@/services/accountOpening';
import { getErrorMessage, getErrorDetails } from '@/utils/errorHandler';

function generateDeviceFingerprint(): string {
  const nav = window.navigator;
  const raw = [
    nav.userAgent,
    nav.language,
    screen.width,
    screen.height,
    new Date().getTimezoneOffset(),
  ].join('|');
  let hash = 0;
  for (let i = 0; i < raw.length; i++) {
    hash = (hash << 5) - hash + raw.charCodeAt(i);
    hash |= 0;
  }
  return `WEB-${Math.abs(hash).toString(16).toUpperCase()}`;
}

// Validação de CPF
const validarCPF = (cpf: string): boolean => {
  cpf = cpf.replace(/[^\d]/g, '');
  
  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;
  
  let soma = 0;
  for (let i = 0; i < 9; i++) {
    soma += parseInt(cpf.charAt(i)) * (10 - i);
  }
  let resto = 11 - (soma % 11);
  let digito1 = resto >= 10 ? 0 : resto;
  
  if (digito1 !== parseInt(cpf.charAt(9))) return false;
  
  soma = 0;
  for (let i = 0; i < 10; i++) {
    soma += parseInt(cpf.charAt(i)) * (11 - i);
  }
  resto = 11 - (soma % 11);
  let digito2 = resto >= 10 ? 0 : resto;
  
  return digito2 === parseInt(cpf.charAt(10));
};

// Validação de CNPJ
const validarCNPJ = (cnpj: string): boolean => {
  cnpj = cnpj.replace(/[^\d]/g, '');
  
  if (cnpj.length !== 14 || /^(\d)\1{13}$/.test(cnpj)) return false;
  
  let tamanho = cnpj.length - 2;
  let numeros = cnpj.substring(0, tamanho);
  let digitos = cnpj.substring(tamanho);
  let soma = 0;
  let pos = tamanho - 7;
  
  for (let i = tamanho; i >= 1; i--) {
    soma += parseInt(numeros.charAt(tamanho - i)) * pos--;
    if (pos < 2) pos = 9;
  }
  
  let resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
  if (resultado !== parseInt(digitos.charAt(0))) return false;
  
  tamanho = tamanho + 1;
  numeros = cnpj.substring(0, tamanho);
  soma = 0;
  pos = tamanho - 7;
  
  for (let i = tamanho; i >= 1; i--) {
    soma += parseInt(numeros.charAt(tamanho - i)) * pos--;
    if (pos < 2) pos = 9;
  }
  
  resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
  return resultado === parseInt(digitos.charAt(1));
};

// Validação de Email
const validarEmail = (email: string): boolean => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};

// Validação de Celular
const validarCelular = (celular: string): boolean => {
  const numeros = celular.replace(/[^\d]/g, '');
  return numeros.length === 11;
};

// Validação de Data de Nascimento (18+ anos)
const validarIdade = (data: string): boolean => {
  const hoje = new Date();
  const nascimento = new Date(data);
  const idade = hoje.getFullYear() - nascimento.getFullYear();
  const mes = hoje.getMonth() - nascimento.getMonth();
  
  if (mes < 0 || (mes === 0 && hoje.getDate() < nascimento.getDate())) {
    return idade - 1 >= 18;
  }
  
  return idade >= 18;
};

export default function CadastroPage() {
  const navigate = useNavigate();
  
  // Estados do formulário
  const [etapaAtual, setEtapaAtual] = useState(1);
  const [tipoDocumento, setTipoDocumento] = useState<'cpf' | 'cnpj'>('cpf');
  const [documento, setDocumento] = useState('');
  const [dataNascimento, setDataNascimento] = useState('');
  const [nome, setNome] = useState('');
  const [nomeSocial, setNomeSocial] = useState('');
  const [situacaoReceita, setSituacaoReceita] = useState('');
  const [razaoSocial, setRazaoSocial] = useState('');
  const [nomeFantasia, setNomeFantasia] = useState('');
  const [dataAbertura, setDataAbertura] = useState('');
  const [situacaoCNPJ, setSituacaoCNPJ] = useState('');
  const [email, setEmail] = useState('');
  const [celular, setCelular] = useState('');
  const [senha, setSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [showSenha, setShowSenha] = useState(false);
  const [showConfirmarSenha, setShowConfirmarSenha] = useState(false);
  const [aceitouTermos, setAceitouTermos] = useState(false);
  const [captchaValidado, setCaptchaValidado] = useState(false);
  const [codigoEmail, setCodigoEmail] = useState('');
  const [codigoCelular, setCodigoCelular] = useState('');

  // Estados de erro e loading
  const [erros, setErros] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [apiErrorDetails, setApiErrorDetails] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [consultandoAPI, setConsultandoAPI] = useState(false);
  const [nomeReadOnly, setNomeReadOnly] = useState(false);
  const [dataNascimentoReadOnly, setDataNascimentoReadOnly] = useState(false);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);

  // Tokens OTP
  const [otpTokenEmail, setOtpTokenEmail] = useState<string | null>(null);
  const [otpTokenCelular, setOtpTokenCelular] = useState<string | null>(null);
  const [redirectCountdown, setRedirectCountdown] = useState(10);
  const [registrationId, setRegistrationId] = useState('');

  const deviceFingerprintRef = useRef<string>('');

  const [mostrarTermos, setMostrarTermos] = useState(false);

  // Força da senha
  const calcularForcaSenha = (s: string): { nivel: number; label: string; cor: string; requisitos: { label: string; atendido: boolean }[] } => {
    if (!s) return { nivel: 0, label: '', cor: '', requisitos: [] };
    let score = 0;
    const reqs = [
      { label: 'Mínimo 8 caracteres', atendido: s.length >= 8 },
      { label: 'Letra maiúscula', atendido: /[A-Z]/.test(s) },
      { label: 'Letra minúscula', atendido: /[a-z]/.test(s) },
      { label: 'Número', atendido: /[0-9]/.test(s) },
      { label: 'Caractere especial', atendido: /[^A-Za-z0-9]/.test(s) },
    ];
    if (s.length >= 8) score++;
    if (s.length >= 12) score++;
    if (/[A-Z]/.test(s)) score++;
    if (/[a-z]/.test(s)) score++;
    if (/[0-9]/.test(s)) score++;
    if (/[^A-Za-z0-9]/.test(s)) score++;
    if (score <= 1) return { nivel: 1, label: 'Muito fraca', cor: 'bg-red-500', requisitos: reqs };
    if (score === 2) return { nivel: 2, label: 'Fraca', cor: 'bg-orange-400', requisitos: reqs };
    if (score === 3) return { nivel: 3, label: 'Média', cor: 'bg-yellow-400', requisitos: reqs };
    if (score === 4) return { nivel: 4, label: 'Forte', cor: 'bg-green-400', requisitos: reqs };
    return { nivel: 5, label: 'Muito forte', cor: 'bg-green-600', requisitos: reqs };
  };

  const forcaSenha = calcularForcaSenha(senha);

  // Máscaras
  const aplicarMascaraCPF = (valor: string) => {
    return valor
      .replace(/\D/g, '')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})/, '$1-$2')
      .replace(/(-\d{2})\d+?$/, '$1');
  };
  
  const aplicarMascaraCNPJ = (valor: string) => {
    return valor
      .replace(/\D/g, '')
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d{1,2})/, '$1-$2')
      .replace(/(-\d{2})\d+?$/, '$1');
  };
  
  const aplicarMascaraCelular = (valor: string) => {
    return valor
      .replace(/\D/g, '')
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .replace(/(-\d{4})\d+?$/, '$1');
  };

  const limparDadosConsulta = useCallback(() => {
    setNome('');
    setNomeSocial('');
    setSituacaoReceita('');
    setRazaoSocial('');
    setNomeFantasia('');
    setDataAbertura('');
    setDataNascimento('');
    setSituacaoCNPJ('');
    setNomeReadOnly(false);
    setDataNascimentoReadOnly(false);
  }, []);

  // Formatar data para YYYY-MM-DD (date input)
  const formatarDataParaInput = (raw: string): string => {
    if (!raw) return '';
    // Já está em formato ISO?
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    // Formato DD/MM/YYYY
    if (raw.includes('/')) {
      const parts = raw.split('/');
      if (parts.length === 3) {
        return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
      }
    }
    // Formato DD-MM-YYYY
    if (raw.includes('-')) {
      const parts = raw.split('-');
      if (parts[0].length === 2 && parts[2]?.length === 4) {
        return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
      }
    }
    // Formato ddMMyyyy (ex: "01011990" vindo do SERPRO)
    if (/^\d{8}$/.test(raw)) {
      const dia = raw.substring(0, 2);
      const mes = raw.substring(2, 4);
      const ano = raw.substring(4, 8);
      return `${ano}-${mes}-${dia}`;
    }
    return raw;
  };

  // Formatar data ISO (YYYY-MM-DD) para exibição DD/MM/YYYY
  const formatarDataParaExibicao = (isoDate: string): string => {
    if (!isoDate) return '';
    const match = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      return `${match[3]}/${match[2]}/${match[1]}`;
    }
    return isoDate;
  };

  // Consulta CPF via API
  const consultarCPF = useCallback(async (cpfLimpo: string) => {
    if (!cpfLimpo || cpfLimpo.length !== 11) return;
    setConsultandoAPI(true);
    setApiError(null);
    setApiErrorDetails([]);
    try {
      const data = await apiConsultarCPF(cpfLimpo);
      const responseJson = typeof data.responseJson === 'string' ? JSON.parse(data.responseJson) : data.responseJson;
      if (responseJson.nome) {
        setNome(responseJson.nome);
        setNomeReadOnly(true);
      }
      if (responseJson.situacao?.descricao) {
        setSituacaoReceita(responseJson.situacao.descricao);
      }
      const dataNasc = responseJson.dataNascimento || responseJson.nascimento || responseJson.data_nascimento;
      if (dataNasc) {
        const dataFormatada = formatarDataParaInput(dataNasc);
        if (dataFormatada) {
          setDataNascimento(dataFormatada);
          setDataNascimentoReadOnly(true);
        }
      }
    } catch (err) {
      const msg = getErrorMessage(err);
      setApiError(msg);
      setApiErrorDetails([]);
    } finally {
      setConsultandoAPI(false);
    }
  }, []);

  const consultarCNPJ = useCallback(async (cnpjLimpo: string) => {
    if (!cnpjLimpo || cnpjLimpo.length !== 14) return;
    setConsultandoAPI(true);
    setApiError(null);
    setApiErrorDetails([]);
    try {
      const data = await apiConsultarCNPJ(cnpjLimpo);
      const responseJson = typeof data.responseJson === 'string' ? JSON.parse(data.responseJson) : data.responseJson;
      if (responseJson.nomeEmpresarial) {
        setNome(responseJson.nomeEmpresarial);
        setRazaoSocial(responseJson.nomeEmpresarial);
        setNomeFantasia(responseJson.nomeFantasia ?? '');
        setDataAbertura(responseJson.dataAbertura ?? '');
        setDataNascimento(responseJson.dataAbertura ?? '');
        setDataNascimentoReadOnly(!!responseJson.dataAbertura);
        setSituacaoCNPJ(responseJson.situacaoCadastral?.codigo === '2' ? 'Ativa' : (responseJson.situacaoCadastral?.descricao ?? responseJson.situacao ?? ''));
        setNomeReadOnly(true);
      }
    } catch (err) {
      const msg = getErrorMessage(err);
      setApiError(msg);
      setApiErrorDetails([]);
    } finally {
      setConsultandoAPI(false);
    }
  }, []);

  // Efeito de consulta CPF/CNPJ
  useEffect(() => {
    const digits = documento.replace(/\D/g, '');
    const expectedLen = tipoDocumento === 'cpf' ? 11 : 14;
    if (digits.length !== expectedLen) return;

    const timer = setTimeout(() => {
      if (tipoDocumento === 'cpf') {
        consultarCPF(digits);
      } else if (tipoDocumento === 'cnpj') {
        consultarCNPJ(digits);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [documento, tipoDocumento, consultarCPF, consultarCNPJ]);

  // Countdown para redirecionamento automático após cadastro
  useEffect(() => {
    if (etapaAtual !== 4) return;
    setRedirectCountdown(10);
    const timer = setInterval(() => {
      setRedirectCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/premiado/login');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [etapaAtual, navigate]);

  const handleDocumentoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valor = e.target.value;
    const mascara = tipoDocumento === 'cpf' ? aplicarMascaraCPF(valor) : aplicarMascaraCNPJ(valor);
    setDocumento(mascara);
    limparDadosConsulta();
    setApiError(null);
    setApiErrorDetails([]);
    if (erros.documento) {
      setErros((prev) => ({ ...prev, documento: '' }));
    }
  };

  const handleCelularChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valor = e.target.value;
    setCelular(aplicarMascaraCelular(valor));
    if (erros.celular) {
      setErros((prev) => ({ ...prev, celular: '' }));
    }
  };

  const validarEtapa1 = () => {
    const novosErros: Record<string, string> = {};

    if (!documento) {
      novosErros.documento = 'Campo obrigatório';
    } else if (tipoDocumento === 'cpf' && !validarCPF(documento)) {
      novosErros.documento = 'CPF inválido';
    } else if (tipoDocumento === 'cnpj' && !validarCNPJ(documento)) {
      novosErros.documento = 'CNPJ inválido';
    }

    if (!dataNascimento) {
      novosErros.dataNascimento = 'Campo obrigatório';
    } else if (tipoDocumento === 'cpf' && !validarIdade(dataNascimento)) {
      novosErros.dataNascimento = 'É necessário ter 18 anos ou mais';
    }

    setErros(novosErros);
    return Object.keys(novosErros).length === 0;
  };

  const validarEtapa2 = () => {
    const novosErros: Record<string, string> = {};

    if (!nome) {
      novosErros.nome = 'Campo obrigatório';
    } else if (nome.length < 3) {
      novosErros.nome = 'Nome deve ter no mínimo 3 caracteres';
    }

    if (!email) {
      novosErros.email = 'Campo obrigatório';
    } else if (!validarEmail(email)) {
      novosErros.email = 'Email inválido';
    }

    if (!celular) {
      novosErros.celular = 'Campo obrigatório';
    } else if (!validarCelular(celular)) {
      novosErros.celular = 'Celular inválido (deve ter 11 dígitos)';
    }

    if (!senha) {
      novosErros.senha = 'Campo obrigatório';
    } else if (senha.length < 8) {
      novosErros.senha = 'A senha deve ter no mínimo 8 caracteres';
    } else if (!/[A-Z]/.test(senha)) {
      novosErros.senha = 'A senha deve conter ao menos uma letra maiúscula';
    } else if (!/[a-z]/.test(senha)) {
      novosErros.senha = 'A senha deve conter ao menos uma letra minúscula';
    } else if (!/[0-9]/.test(senha)) {
      novosErros.senha = 'A senha deve conter ao menos um número';
    } else if (!/[^A-Za-z0-9]/.test(senha)) {
      novosErros.senha = 'A senha deve conter ao menos um caractere especial (!@#$% etc)';
    }

    if (!confirmarSenha) {
      novosErros.confirmarSenha = 'Campo obrigatório';
    } else if (senha !== confirmarSenha) {
      novosErros.confirmarSenha = 'As senhas não coincidem';
    }

    if (!aceitouTermos) {
      novosErros.termos = 'Você deve aceitar os termos para continuar';
    }

    if (!captchaValidado) {
      novosErros.captcha = 'Valide o captcha';
    }

    setErros(novosErros);
    return Object.keys(novosErros).length === 0;
  };

  const enviarOTP = async (canal: 'email' | 'sms', destino: string): Promise<{ success: boolean; token?: string; message?: string; details?: string[] }> => {
    try {
      const data = await sendOtp({
        canal: canal === 'email' ? 'EMAIL' : 'SMS',
        destino,
        name: nome,
        deviceFingerprint: deviceFingerprintRef.current,
      });
      return { success: true, token: data.otpToken };
    } catch (err) {
      const msg = getErrorMessage(err);
      const { details } = getErrorDetails(err);
      return { success: false, message: msg, details };
    }
  };

  const validarOTP = async (canal: 'email' | 'sms', destino: string, codigo: string, token: string): Promise<{ success: boolean; message?: string; details?: string[] }> => {
    try {
      await validateOtp({
        canal: canal === 'email' ? 'EMAIL' : 'SMS',
        destino,
        codigo,
        otpToken: token,
        deviceFingerprint: deviceFingerprintRef.current,
      });
      return { success: true };
    } catch (err) {
      const msg = getErrorMessage(err);
      const { details } = getErrorDetails(err);
      return { success: false, message: msg, details };
    }
  };

  const handleEnviarOTPs = async () => {
    setIsLoading(true);
    setApiError(null);
    setApiErrorDetails([]);

    const celularLimpo = celular.replace(/\D/g, '');

    const [emailResult, celularResult] = await Promise.all([
      enviarOTP('email', email),
      enviarOTP('sms', celularLimpo),
    ]);

    if (!emailResult.success) {
      setApiError(emailResult.message ?? 'Erro ao enviar código para email');
      setApiErrorDetails(emailResult.details ?? []);
      setIsLoading(false);
      return;
    }

    if (!celularResult.success) {
      setApiError(celularResult.message ?? 'Erro ao enviar código para celular');
      setApiErrorDetails(celularResult.details ?? []);
      setIsLoading(false);
      return;
    }

    setOtpTokenEmail(emailResult.token ?? null);
    setOtpTokenCelular(celularResult.token ?? null);
    setEtapaAtual(3);
    setIsLoading(false);
  };

  const handleRegisterStart = async () => {
    if (!validarEtapa1()) return;

    if (!deviceFingerprintRef.current) {
      deviceFingerprintRef.current = generateDeviceFingerprint();
    }

    const documentoLimpo = documento.replace(/\D/g, '');

    setIsLoading(true);
    setApiError(null);
    setApiErrorDetails([]);

    try {
      const data = await startRegistration({
        tipoDocumento,
        documento: documentoLimpo,
        dataNascimento,
        deviceFingerprint: deviceFingerprintRef.current,
      });

      setRegistrationId(data.registrationId);
      if (data.nome && !nomeReadOnly) {
        setNome(data.nome);
      }
      if (data.email) setEmail(data.email);
      if (data.celular) setCelular(data.celular);
      setEtapaAtual(2);
      setApiError(null);
      setApiErrorDetails([]);
    } catch (err) {
      const msg = getErrorMessage(err);
      const { details } = getErrorDetails(err);
      setApiError(msg);
      setApiErrorDetails(details ?? []);
    } finally {
      setIsLoading(false);
    }
  };

  const avancarEtapa = () => {
    if (etapaAtual === 1) {
      handleRegisterStart();
    } else if (etapaAtual === 2 && validarEtapa2()) {
      if (!deviceFingerprintRef.current) {
        deviceFingerprintRef.current = generateDeviceFingerprint();
      }
      handleEnviarOTPs();
    }
  };

  const voltarEtapa = () => {
    if (etapaAtual > 1) {
      setEtapaAtual(etapaAtual - 1);
      setErros({});
      setApiError(null);
      setApiErrorDetails([]);
    }
  };

  const handleReenviarCodigo = async (tipo: 'email' | 'celular') => {
    setIsLoading(true);
    setApiError(null);
    setApiErrorDetails([]);

    const destino = tipo === 'email' ? email : celular.replace(/\D/g, '');
    const canal = tipo === 'email' ? 'email' : 'sms';

    const result = await enviarOTP(canal, destino);
    if (result.success) {
      if (tipo === 'email') setOtpTokenEmail(result.token ?? null);
      else setOtpTokenCelular(result.token ?? null);
    } else {
      setApiError(result.message ?? `Erro ao reenviar código para ${tipo}`);
      setApiErrorDetails(result.details ?? []);
    }
    setIsLoading(false);
  };

  const handleConfirmCodes = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError(null);
    setApiErrorDetails([]);

    if (!codigoEmail || !codigoCelular) {
      setApiError('Por favor, preencha ambos os códigos de verificação');
      return;
    }

    if (codigoEmail.length !== 6 || codigoCelular.length !== 6) {
      setApiError('Os códigos devem ter 6 dígitos');
      return;
    }

    if (!otpTokenEmail || !otpTokenCelular) {
      setApiError('Tokens de verificação não encontrados. Volte e tente novamente.');
      return;
    }

    setIsLoading(true);

    const celularLimpo = celular.replace(/\D/g, '');

    const [emailResult, celularResult] = await Promise.all([
      validarOTP('email', email, codigoEmail, otpTokenEmail),
      validarOTP('sms', celularLimpo, codigoCelular, otpTokenCelular),
    ]);

    if (!emailResult.success) {
      setApiError(emailResult.message ?? 'Código de email inválido');
      setApiErrorDetails(emailResult.details ?? []);
      setIsLoading(false);
      return;
    }

    if (!celularResult.success) {
      setApiError(celularResult.message ?? 'Código de celular inválido');
      setApiErrorDetails(celularResult.details ?? []);
      setIsLoading(false);
      return;
    }

    // Cadastro final
    const documentoLimpo = documento.replace(/\D/g, '');
    try {
      await finalizeRegistration({
        tipoDocumento,
        documento: documentoLimpo,
        dataNascimento: tipoDocumento === 'cpf' ? dataNascimento : undefined,
        nome,
        email,
        celular: celularLimpo,
        senha,
        confirmarSenha,
        deviceFingerprint: deviceFingerprintRef.current,
        emailOtpToken: otpTokenEmail,
        celularOtpToken: otpTokenCelular,
      });
      setEtapaAtual(4);
      setShowSuccessAnimation(true);
    } catch (err) {
      const msg = getErrorMessage(err);
      const { details } = getErrorDetails(err);
      setApiError(msg);
      setApiErrorDetails(details ?? []);
    }

    setIsLoading(false);
  };

  // ... existing code ...

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-orange-500 via-orange-600 to-red-600 p-16 flex-col justify-between relative overflow-hidden">
        {/* Animated Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-white rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/5 rounded-full -ml-48 -mb-48"></div>

        {/* Content */}
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center">
              <i className="ri-trophy-line text-2xl text-orange-500"></i>
            </div>
            <h1 className="text-3xl font-bold text-white">Cadastro do Premiado</h1>
          </div>
          
          <h2 className="text-4xl font-bold text-white mb-6 leading-tight">
            Transforme seu desempenho em recompensas
          </h2>
          <p className="text-xl text-white/90 leading-relaxed">
            Participe de campanhas exclusivas, acumule pontos e troque por prêmios incríveis. 
            Sua jornada de sucesso começa aqui!
          </p>
        </div>

        {/* QR Code and App Download Section */}
        <div className="relative z-10 bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
          <div className="text-center mb-6">
            <h3 className="text-2xl font-bold text-white mb-2">Cadastre-se pelo App</h3>
            <p className="text-white/90 text-sm">Escaneie o QR Code ou baixe o aplicativo</p>
          </div>

          {/* QR Code */}
          <div className="flex justify-center mb-4">
            <div className="bg-white p-4 rounded-2xl shadow-2xl">
              <img 
                src="https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=https://incentiv.me/app/cadastro/XXX-12345" 
                alt="QR Code para cadastro"
                className="w-44 h-44"
              />
            </div>
          </div>

          {/* Campaign Code */}
          <div className="text-center mb-6">
            <p className="text-white/80 text-sm mb-1">Código da Campanha</p>
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-6 py-3 rounded-xl border border-white/30">
              <i className="ri-key-2-line text-white text-lg"></i>
              <span className="text-white font-bold text-xl tracking-wider">XXX-12345</span>
            </div>
          </div>

          {/* App Download Section */}
          <div className="mt-8 pt-8 border-t border-white/10">
            <p className="text-white/80 text-sm mb-4 text-center">
              Baixe nosso app e cadastre-se facilmente
            </p>
            
            {/* App Store Buttons - Side by Side */}
            <div className="flex gap-3 justify-center">
              <a
                href="https://apps.apple.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 max-w-[140px] bg-black hover:bg-gray-900 transition-colors rounded-lg px-4 py-2.5 flex items-center gap-2"
              >
                <i className="ri-apple-fill text-white text-2xl"></i>
                <div className="text-left">
                  <div className="text-[10px] text-white/70 leading-tight">Baixar na</div>
                  <div className="text-sm font-semibold text-white leading-tight">App Store</div>
                </div>
              </a>
              
              <a
                href="https://play.google.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 max-w-[140px] bg-black hover:bg-gray-900 transition-colors rounded-lg px-4 py-2.5 flex items-center gap-2"
              >
                <i className="ri-google-play-fill text-white text-2xl"></i>
                <div className="text-left">
                  <div className="text-[10px] text-white/70 leading-tight">Disponível no</div>
                  <div className="text-sm font-semibold text-white leading-tight">Google Play</div>
                </div>
              </a>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="relative z-10 grid grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-3 border border-white/30">
              <i className="ri-gift-line text-3xl text-white"></i>
            </div>
            <p className="text-white/90 font-medium">Prêmios Exclusivos</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-3 border border-white/30">
              <i className="ri-line-chart-line text-3xl text-white"></i>
            </div>
            <p className="text-white/90 font-medium">Acompanhe seu Progresso</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-3 border border-white/30">
              <i className="ri-team-line text-3xl text-white"></i>
            </div>
            <p className="text-white/90 font-medium">Comunidade Ativa</p>
          </div>
        </div>
      </div>

      {/* Right Side - Cadastro Form */}
      <div className="flex-1 flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-10">
            <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg">
              <i className="ri-trophy-line text-3xl text-white"></i>
            </div>
            <div>
              <span className="text-2xl font-bold text-gray-900 block">Área do Premiado</span>
              <span className="text-gray-500 text-sm">Sua jornada de conquistas</span>
            </div>
          </div>

          {/* Formulário de Cadastro */}
          {etapaAtual <= 2 && (
            <div className="bg-white rounded-3xl shadow-2xl p-10 border border-gray-100 relative overflow-hidden">
              {/* Loading Overlay - Consulta em andamento */}
              {consultandoAPI && (
                <div className="absolute inset-0 bg-white/90 backdrop-blur-sm z-20 flex flex-col items-center justify-center animate-in fade-in duration-300">
                  <div className="relative mb-6">
                    {/* Spinner animado */}
                    <div className="w-20 h-20 rounded-full border-4 border-gray-100 border-t-orange-500 animate-spin"></div>
                    {/* Ícone central */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <i className={`${tipoDocumento === 'cpf' ? 'ri-user-search-line' : 'ri-building-4-line'} text-3xl text-orange-500 w-10 h-10 flex items-center justify-center`}></i>
                    </div>
                  </div>

                  <h3 className="text-lg font-bold text-gray-800 mb-2 text-center">
                    {tipoDocumento === 'cpf' ? 'Consultando CPF...' : 'Consultando CNPJ...'}
                  </h3>
                  <p className="text-sm text-gray-500 text-center max-w-xs mb-6">
                    {tipoDocumento === 'cpf'
                      ? 'Estamos consultando os dados do CPF junto à Receita Federal (SERPRO) para preencher seu nome automaticamente.'
                      : 'Estamos consultando os dados do CNPJ junto à Receita Federal para preencher os dados da empresa automaticamente.'}
                  </p>

                  {/* Barrinha de progresso com steps */}
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-orange-500 animate-pulse"></div>
                    <div className="w-2 h-2 rounded-full bg-orange-400 animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                    <div className="w-2 h-2 rounded-full bg-orange-300 animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                    <div className="w-2 h-2 rounded-full bg-orange-200 animate-pulse" style={{ animationDelay: '0.6s' }}></div>
                  </div>

                  <div className="mt-6 flex items-center gap-2 px-4 py-2 bg-orange-50 rounded-full">
                    <i className="ri-shield-check-line text-orange-500 w-4 h-4 flex items-center justify-center"></i>
                    <span className="text-xs text-orange-700 font-medium">Consulta segura e oficial</span>
                  </div>
                </div>
              )}

              <div className="mb-10">
                <div className="flex justify-center mb-6">
                  <img 
                    src="https://static.readdy.ai/image/22e92c18f0b031d861a5e53bf8e362fb/6356cf72c36f3ef424056fffdf6d6f31.png" 
                    alt="GetPoints Logo" 
                    className="h-12 w-auto"
                  />
                </div>
                <h2 className="text-2xl font-bold text-slate-800 mb-2 text-center">
                  Criar Nova Conta
                </h2>
                <p className="text-gray-600 text-base">Preencha os dados abaixo para começar</p>
              </div>

              <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
                {/* ETAPA 1 */}
                {etapaAtual === 1 && (
                  <>
                    {/* Tipo de Documento */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Tipo de Documento
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => {
                            setTipoDocumento('cpf');
                            setDocumento('');
                            limparDadosConsulta();
                            setDataNascimento('');
                            setErros((prev) => ({ ...prev, documento: '' }));
                          }}
                          className={`p-3 rounded-xl border-2 transition-all ${
                            tipoDocumento === 'cpf'
                              ? 'border-orange-500 bg-orange-50'
                              : 'border-gray-200 hover:border-orange-300'
                          }`}
                        >
                          <i className="ri-user-line text-xl mb-1 w-5 h-5 flex items-center justify-center mx-auto"></i>
                          <div className="font-semibold text-sm">CPF</div>
                          <div className="text-xs text-gray-500">Pessoa Física</div>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setTipoDocumento('cnpj');
                            setDocumento('');
                            limparDadosConsulta();
                            setDataNascimento('');
                            setErros((prev) => ({ ...prev, documento: '' }));
                          }}
                          className={`p-3 rounded-xl border-2 transition-all ${
                            tipoDocumento === 'cnpj'
                              ? 'border-orange-500 bg-orange-50'
                              : 'border-gray-200 hover:border-orange-300'
                          }`}
                        >
                          <i className="ri-building-line text-xl mb-1 w-5 h-5 flex items-center justify-center mx-auto"></i>
                          <div className="font-semibold text-sm">CNPJ</div>
                          <div className="text-xs text-gray-500">Pessoa Jurídica</div>
                        </button>
                      </div>
                    </div>

                    {/* Número do Documento */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {tipoDocumento === 'cpf' ? 'CPF' : 'CNPJ'}
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <i className={`ri-id-card-line text-lg ${consultandoAPI ? 'text-orange-500' : 'text-gray-400'} w-5 h-5 flex items-center justify-center`}></i>
                        </div>
                        <input
                          type="text"
                          value={documento}
                          onChange={handleDocumentoChange}
                          placeholder={tipoDocumento === 'cpf' ? '000.000.000-00' : '00.000.000/0000-00'}
                          maxLength={tipoDocumento === 'cpf' ? 14 : 18}
                          disabled={consultandoAPI}
                          className={`block w-full pl-12 pr-4 py-3.5 border ${erros.documento ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'} rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent focus:bg-white transition-all text-base ${consultandoAPI ? 'opacity-50 cursor-not-allowed' : ''}`}
                        />
                        {consultandoAPI && (
                          <div className="absolute inset-y-0 right-0 pr-4 flex items-center">
                            <i className="ri-loader-4-line animate-spin text-orange-500 w-5 h-5 flex items-center justify-center"></i>
                          </div>
                        )}
                      </div>
                      {erros.documento && (
                        <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                          <i className="ri-error-warning-line"></i>
                          {erros.documento}
                        </p>
                      )}
                    </div>

                    {/* Data de Nascimento / Constituição */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {tipoDocumento === 'cpf' ? 'Data de Nascimento' : 'Data de Constituição'}
                      </label>
                      {dataNascimentoReadOnly ? (
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <i className="ri-calendar-line text-lg text-gray-400 w-5 h-5 flex items-center justify-center"></i>
                          </div>
                          <div className="block w-full pl-12 pr-12 py-3.5 border border-gray-200 bg-gray-100 rounded-xl text-base text-gray-700">
                            {formatarDataParaExibicao(dataNascimento)}
                          </div>
                          <div className="absolute inset-y-0 right-0 pr-4 flex items-center">
                            <i className="ri-lock-line text-gray-400 w-5 h-5 flex items-center justify-center" title="Data consultada via Receita Federal"></i>
                          </div>
                        </div>
                      ) : (
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <i className="ri-calendar-line text-lg text-gray-400 w-5 h-5 flex items-center justify-center"></i>
                          </div>
                          <input
                            type="date"
                            value={dataNascimento}
                            onChange={(e) => {
                              setDataNascimento(e.target.value);
                              if (erros.dataNascimento) setErros((prev) => ({ ...prev, dataNascimento: '' }));
                            }}
                            disabled={consultandoAPI}
                            className={`block w-full pl-12 pr-4 py-3.5 border ${erros.dataNascimento ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'} rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent focus:bg-white transition-all text-base ${consultandoAPI ? 'opacity-50 cursor-not-allowed' : ''}`}
                          />
                        </div>
                      )}
                      {erros.dataNascimento && (
                        <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                          <i className="ri-error-warning-line w-4 h-4 flex items-center justify-center"></i>
                          {erros.dataNascimento}
                        </p>
                      )}
                    </div>

                    {/* Dados consultados (CPF) */}
                    {tipoDocumento === 'cpf' && (nomeSocial || situacaoReceita) && (
                      <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-1">
                        {nomeSocial && (
                          <p className="text-sm text-green-700">
                            <span className="font-semibold">Nome social:</span> {nomeSocial}
                          </p>
                        )}
                        {situacaoReceita && (
                          <p className="text-sm text-green-700">
                            <span className="font-semibold">Situação Receita:</span> {situacaoReceita}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Dados consultados (CNPJ) */}
                    {tipoDocumento === 'cnpj' && (nomeFantasia || dataAbertura || situacaoCNPJ) && (
                      <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-1">
                        {nomeFantasia && (
                          <p className="text-sm text-green-700">
                            <span className="font-semibold">Nome fantasia:</span> {nomeFantasia}
                          </p>
                        )}
                        {dataAbertura && (
                          <p className="text-sm text-green-700">
                            <span className="font-semibold">Abertura:</span> {dataAbertura}
                          </p>
                        )}
                        {situacaoCNPJ && (
                          <p className="text-sm text-green-700">
                            <span className="font-semibold">Situação:</span> {situacaoCNPJ}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Botão Avançar */}
                    {/* API Error Banner */}
                    {apiError && (
                      <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
                        <i className="ri-error-warning-fill text-red-500 text-lg flex-shrink-0 mt-0.5 w-5 h-5 flex items-center justify-center"></i>
                        <div className="flex-1">
                          <p className="text-sm text-red-700 leading-relaxed font-medium">{apiError}</p>
                          {apiErrorDetails.length > 0 && (
                            <ul className="mt-2 space-y-1">
                              {apiErrorDetails.map((detail, idx) => (
                                <li key={idx} className="flex items-start gap-2 text-sm text-red-600">
                                  <i className="ri-close-circle-line mt-0.5 flex-shrink-0 text-red-400 w-4 h-4 flex items-center justify-center"></i>
                                  <span>{detail}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={avancarEtapa}
                      disabled={consultandoAPI || isLoading}
                      className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-4 px-6 rounded-xl font-semibold text-base hover:from-orange-600 hover:to-orange-700 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all flex items-center justify-center gap-2 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {consultandoAPI || isLoading ? (
                        <>
                          <i className="ri-loader-4-line animate-spin text-xl w-5 h-5 flex items-center justify-center"></i>
                          {consultandoAPI ? 'Consultando...' : 'Iniciando cadastro...'}
                        </>
                      ) : (
                        <>
                          Avançar
                          <i className="ri-arrow-right-line text-xl w-5 h-5 flex items-center justify-center"></i>
                        </>
                      )}
                    </button>
                  </>
                )}

                {/* ETAPA 2 */}
                {etapaAtual === 2 && (
                  <>
                    {/* Nome */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {tipoDocumento === 'cpf' ? 'Nome Completo' : 'Razão Social'}
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <i className="ri-user-line text-lg text-gray-400 w-5 h-5 flex items-center justify-center"></i>
                        </div>
                        <input
                          type="text"
                          value={nome}
                          onChange={(e) => {
                            if (!nomeReadOnly) {
                              setNome(e.target.value);
                              if (erros.nome) setErros((prev) => ({ ...prev, nome: '' }));
                            }
                          }}
                          placeholder={tipoDocumento === 'cpf' ? 'Seu nome completo' : 'Nome da empresa'}
                          readOnly={nomeReadOnly}
                          className={`block w-full pl-12 pr-4 py-3.5 border ${erros.nome ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'} rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent focus:bg-white transition-all text-base ${nomeReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                        />
                        {nomeReadOnly && (
                          <div className="absolute inset-y-0 right-0 pr-4 flex items-center">
                            <i className="ri-lock-line text-gray-400 w-5 h-5 flex items-center justify-center" title="Nome consultado via Receita Federal"></i>
                          </div>
                        )}
                      </div>
                      {erros.nome && (
                        <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                          <i className="ri-error-warning-line w-4 h-4 flex items-center justify-center"></i>
                          {erros.nome}
                        </p>
                      )}
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        E-mail
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <i className="ri-mail-line text-lg text-gray-400 w-5 h-5 flex items-center justify-center"></i>
                        </div>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => {
                            setEmail(e.target.value);
                            if (erros.email) setErros((prev) => ({ ...prev, email: '' }));
                          }}
                          placeholder="seu@email.com"
                          className={`block w-full pl-12 pr-4 py-3.5 border ${erros.email ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'} rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent focus:bg-white transition-all text-base`}
                        />
                      </div>
                      {erros.email && (
                        <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                          <i className="ri-error-warning-line w-4 h-4 flex items-center justify-center"></i>
                          {erros.email}
                        </p>
                      )}
                    </div>

                    {/* Celular */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Celular
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <i className="ri-smartphone-line text-lg text-gray-400 w-5 h-5 flex items-center justify-center"></i>
                        </div>
                        <input
                          type="tel"
                          value={celular}
                          onChange={handleCelularChange}
                          placeholder="(00) 00000-0000"
                          maxLength={15}
                          className={`block w-full pl-12 pr-4 py-3.5 border ${erros.celular ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'} rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent focus:bg-white transition-all text-base`}
                        />
                      </div>
                      {erros.celular && (
                        <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                          <i className="ri-error-warning-line w-4 h-4 flex items-center justify-center"></i>
                          {erros.celular}
                        </p>
                      )}
                    </div>

                    {/* Senha */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Senha
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <i className="ri-lock-line text-lg text-gray-400 w-5 h-5 flex items-center justify-center"></i>
                        </div>
                        <input
                          type={showSenha ? 'text' : 'password'}
                          value={senha}
                          onChange={(e) => {
                            setSenha(e.target.value);
                            if (erros.senha) setErros((prev) => ({ ...prev, senha: '' }));
                          }}
                          placeholder="Mínimo 8 caracteres"
                          className={`block w-full pl-12 pr-12 py-3.5 border ${erros.senha ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'} rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent focus:bg-white transition-all text-base`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowSenha(!showSenha)}
                          className="absolute inset-y-0 right-0 pr-4 flex items-center"
                        >
                          <i className={`${showSenha ? 'ri-eye-off-line' : 'ri-eye-line'} text-lg text-gray-400 hover:text-gray-600 w-5 h-5 flex items-center justify-center`}></i>
                        </button>
                      </div>
                      {/* Indicador de força + checklist de requisitos */}
                      {senha && (
                        <div className="mt-2">
                          <div className="flex gap-1 mb-1">
                            {[1, 2, 3, 4, 5].map((n) => (
                              <div
                                key={n}
                                className={`h-1.5 flex-1 rounded-full transition-all ${n <= forcaSenha.nivel ? forcaSenha.cor : 'bg-gray-200'}`}
                              />
                            ))}
                          </div>
                          <p className={`text-xs font-medium mb-2 ${
                            forcaSenha.nivel <= 2 ? 'text-red-500' :
                            forcaSenha.nivel === 3 ? 'text-yellow-600' : 'text-green-600'
                          }`}>
                            {forcaSenha.label}
                          </p>
                          <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                            {forcaSenha.requisitos.map((req, idx) => (
                              <div key={idx} className={`flex items-center gap-1.5 text-xs ${req.atendido ? 'text-green-600' : 'text-gray-400'}`}>
                                <i className={`${req.atendido ? 'ri-check-line' : 'ri-close-line'} w-3.5 h-3.5 flex items-center justify-center`}></i>
                                <span>{req.label}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {erros.senha && (
                        <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                          <i className="ri-error-warning-line w-4 h-4 flex items-center justify-center"></i>
                          {erros.senha}
                        </p>
                      )}
                      {!senha && (
                        <p className="mt-1.5 text-xs text-gray-500">
                          Mínimo 8 caracteres com maiúscula, minúscula, número e caractere especial
                        </p>
                      )}
                    </div>

                    {/* Confirmar Senha */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Confirmar Senha
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <i className="ri-lock-2-line text-lg text-gray-400 w-5 h-5 flex items-center justify-center"></i>
                        </div>
                        <input
                          type={showConfirmarSenha ? 'text' : 'password'}
                          value={confirmarSenha}
                          onChange={(e) => {
                            setConfirmarSenha(e.target.value);
                            if (erros.confirmarSenha) setErros((prev) => ({ ...prev, confirmarSenha: '' }));
                          }}
                          placeholder="Repita a senha"
                          className={`block w-full pl-12 pr-12 py-3.5 border ${erros.confirmarSenha ? 'border-red-300 bg-red-50' : confirmarSenha && senha === confirmarSenha ? 'border-green-400 bg-green-50' : 'border-gray-200 bg-gray-50'} rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent focus:bg-white transition-all text-base`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmarSenha(!showConfirmarSenha)}
                          className="absolute inset-y-0 right-0 pr-4 flex items-center"
                        >
                          <i className={`${showConfirmarSenha ? 'ri-eye-off-line' : 'ri-eye-line'} text-lg text-gray-400 hover:text-gray-600 w-5 h-5 flex items-center justify-center`}></i>
                        </button>
                      </div>
                      {confirmarSenha && senha === confirmarSenha && (
                        <p className="mt-1 text-sm text-green-600 flex items-center gap-1">
                          <i className="ri-check-line w-4 h-4 flex items-center justify-center"></i>
                          Senhas coincidem
                        </p>
                      )}
                      {erros.confirmarSenha && (
                        <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                          <i className="ri-error-warning-line w-4 h-4 flex items-center justify-center"></i>
                          {erros.confirmarSenha}
                        </p>
                      )}
                    </div>

                    {/* Aceite dos Termos */}
                    <div>
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={aceitouTermos}
                          onChange={(e) => {
                            setAceitouTermos(e.target.checked);
                            if (erros.termos) setErros((prev) => ({ ...prev, termos: '' }));
                          }}
                          className="mt-1 w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500 cursor-pointer"
                        />
                        <span className="text-sm text-gray-700">
                          Li e aceito os{' '}
                          <button
                            type="button"
                            onClick={() => setMostrarTermos(true)}
                            className="text-orange-600 hover:text-orange-700 font-semibold"
                          >
                            Termos de Adesão
                          </button>
                          {' '}e a Política de Privacidade
                        </span>
                      </label>
                      {erros.termos && (
                        <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                          <i className="ri-error-warning-line w-4 h-4 flex items-center justify-center"></i>
                          {erros.termos}
                        </p>
                      )}
                    </div>

                    {/* Captcha */}
                    <div>
                      <div className={`border-2 ${erros.captcha ? 'border-red-300' : 'border-gray-200'} rounded-xl p-4 bg-gray-50`}>
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={captchaValidado}
                            onChange={(e) => {
                              setCaptchaValidado(e.target.checked);
                              if (erros.captcha) setErros((prev) => ({ ...prev, captcha: '' }));
                            }}
                            className="w-6 h-6 rounded border-gray-300 text-orange-500 focus:ring-orange-500 cursor-pointer"
                          />
                          <span className="text-sm text-gray-700 font-medium">
                            Não sou um robô
                          </span>
                          <div className="ml-auto">
                            <img
                              src="https://www.gstatic.com/recaptcha/api2/logo_48.png"
                              alt="reCAPTCHA"
                              className="h-8"
                            />
                          </div>
                        </label>
                      </div>
                      {erros.captcha && (
                        <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                          <i className="ri-error-warning-line w-4 h-4 flex items-center justify-center"></i>
                          {erros.captcha}
                        </p>
                      )}
                    </div>

                    {/* Botões */}
                    {/* API Error Banner */}
                    {apiError && (
                      <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
                        <i className="ri-error-warning-fill text-red-500 text-lg flex-shrink-0 mt-0.5 w-5 h-5 flex items-center justify-center"></i>
                        <div className="flex-1">
                          <p className="text-sm text-red-700 leading-relaxed font-medium">{apiError}</p>
                          {apiErrorDetails.length > 0 && (
                            <ul className="mt-2 space-y-1">
                              {apiErrorDetails.map((detail, idx) => (
                                <li key={idx} className="flex items-start gap-2 text-sm text-red-600">
                                  <i className="ri-close-circle-line mt-0.5 flex-shrink-0 text-red-400 w-4 h-4 flex items-center justify-center"></i>
                                  <span>{detail}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    )}
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={voltarEtapa}
                        className="flex-1 bg-white border-2 border-gray-200 text-gray-700 py-4 px-6 rounded-xl font-semibold text-base hover:border-orange-500 hover:text-orange-600 hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all flex items-center justify-center gap-2 whitespace-nowrap"
                      >
                        <i className="ri-arrow-left-line text-xl w-5 h-5 flex items-center justify-center"></i>
                        Voltar
                      </button>
                      <button
                        type="button"
                        onClick={avancarEtapa}
                        disabled={isLoading}
                        className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 text-white py-4 px-6 rounded-xl font-semibold text-base hover:from-orange-600 hover:to-orange-700 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
                      >
                        {isLoading ? (
                          <>
                            <i className="ri-loader-4-line animate-spin text-xl w-5 h-5 flex items-center justify-center"></i>
                            Enviando códigos...
                          </>
                        ) : (
                          <>
                            Avançar
                            <i className="ri-arrow-right-line text-xl w-5 h-5 flex items-center justify-center"></i>
                          </>
                        )}
                      </button>
                    </div>
                  </>
                )}

                {/* Divider */}
                {etapaAtual === 1 && (
                  <div className="relative py-4">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-4 bg-white text-gray-500">Já tem uma conta?</span>
                    </div>
                  </div>
                )}

                {/* Botão de Login */}
                {etapaAtual === 1 && (
                  <button
                    type="button"
                    onClick={() => navigate('/premiado/login')}
                    className="w-full bg-white border-2 border-gray-200 text-gray-700 py-4 px-6 rounded-xl font-semibold text-base hover:border-orange-500 hover:text-orange-600 hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all flex items-center justify-center gap-2 whitespace-nowrap"
                  >
                    <i className="ri-login-box-line text-xl"></i>
                    Fazer Login
                  </button>
                )}
              </form>
            </div>
          )}

          {/* Tela de Validação de Tokens - ETAPA 3 */}
          {etapaAtual === 3 && (
            <div className="bg-white rounded-3xl shadow-2xl p-10 border border-gray-100">
              <div className="mb-10">
                <div className="flex justify-center mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <i className="ri-shield-check-line text-4xl text-white w-10 h-10 flex items-center justify-center"></i>
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-slate-800 mb-2 text-center">
                  Validação de Segurança
                </h2>
                <p className="text-gray-600 text-base text-center">
                  Enviamos códigos de validação para seu email e celular
                </p>
              </div>

              <form onSubmit={handleConfirmCodes} className="space-y-6">
                {/* Código do Email */}
                <div className="bg-orange-50 rounded-xl p-6 border border-orange-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
                      <i className="ri-mail-line text-xl text-white w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-800">Código do Email</div>
                      <div className="text-sm text-gray-600">{email}</div>
                    </div>
                  </div>
                  <input
                    type="text"
                    value={codigoEmail}
                    onChange={(e) => {
                      setCodigoEmail(e.target.value.replace(/\D/g, '').slice(0, 6));
                      setApiError(null);
                      setApiErrorDetails([]);
                    }}
                    placeholder="000000"
                    maxLength={6}
                    className="w-full px-4 py-3 rounded-xl border-2 border-orange-200 focus:border-orange-500 focus:outline-none text-center text-2xl font-bold tracking-widest"
                  />
                  <button
                    type="button"
                    onClick={() => handleReenviarCodigo('email')}
                    disabled={isLoading}
                    className="mt-3 text-sm text-orange-600 hover:text-orange-700 font-semibold disabled:opacity-50"
                  >
                    Reenviar código
                  </button>
                </div>

                {/* Código do Celular */}
                <div className="bg-orange-50 rounded-xl p-6 border border-orange-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
                      <i className="ri-smartphone-line text-xl text-white w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-800">Código do Celular</div>
                      <div className="text-sm text-gray-600">{celular}</div>
                    </div>
                  </div>
                  <input
                    type="text"
                    value={codigoCelular}
                    onChange={(e) => {
                      setCodigoCelular(e.target.value.replace(/\D/g, '').slice(0, 6));
                      setApiError(null);
                      setApiErrorDetails([]);
                    }}
                    placeholder="000000"
                    maxLength={6}
                    className="w-full px-4 py-3 rounded-xl border-2 border-orange-200 focus:border-orange-500 focus:outline-none text-center text-2xl font-bold tracking-widest"
                  />
                  <button
                    type="button"
                    onClick={() => handleReenviarCodigo('celular')}
                    disabled={isLoading}
                    className="mt-3 text-sm text-orange-600 hover:text-orange-700 font-semibold disabled:opacity-50"
                  >
                    Reenviar código
                  </button>
                </div>

                {/* API Error na validação */}
                {apiError && (
                  <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 flex items-start gap-3">
                    <i className="ri-error-warning-line text-2xl text-red-600 w-6 h-6 flex items-center justify-center flex-shrink-0"></i>
                    <div className="flex-1">
                      <p className="text-sm text-red-700 font-medium">{apiError}</p>
                      {apiErrorDetails.length > 0 && (
                        <ul className="mt-2 space-y-1">
                          {apiErrorDetails.map((detail, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-red-600">
                              <i className="ri-close-circle-line mt-0.5 flex-shrink-0 text-red-400 w-4 h-4 flex items-center justify-center"></i>
                              <span>{detail}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                )}

                {/* Botão de Confirmação */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-4 px-6 rounded-xl font-semibold text-base hover:from-orange-600 hover:to-orange-700 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  {isLoading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-xl w-5 h-5 flex items-center justify-center"></i>
                      Validando...
                    </>
                  ) : (
                    <>
                      Confirmar Códigos
                      <i className="ri-check-line text-xl w-5 h-5 flex items-center justify-center"></i>
                    </>
                  )}
                </button>

                {/* Botão Voltar */}
                <button
                  type="button"
                  onClick={() => setEtapaAtual(2)}
                  className="w-full bg-white border-2 border-gray-200 text-gray-700 py-4 px-6 rounded-xl font-semibold text-base hover:border-orange-500 hover:text-orange-600 hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line text-xl w-5 h-5 flex items-center justify-center"></i>
                  Voltar
                </button>
              </form>
            </div>
          )}

          {/* Tela de Sucesso - ETAPA 4 */}
          {etapaAtual === 4 && (
            <div className="bg-white rounded-3xl shadow-2xl p-10 border border-gray-100 relative overflow-hidden animate-in fade-in zoom-in duration-500">
              {/* Background celebration particles */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                {Array.from({ length: 12 }).map((_, i) => (
                  <div
                    key={i}
                    className="absolute rounded-full opacity-20 animate-pulse"
                    style={{
                      left: `${10 + Math.random() * 80}%`,
                      top: `${10 + Math.random() * 80}%`,
                      width: `${30 + Math.random() * 60}px`,
                      height: `${30 + Math.random() * 60}px`,
                      backgroundColor: ['#f97316', '#fb923c', '#fdba74', '#22c55e', '#10b981'][i % 5],
                      animationDelay: `${Math.random() * 2}s`,
                      animationDuration: `${2 + Math.random() * 2}s`,
                    }}
                  />
                ))}
              </div>

              <div className="relative z-10 text-center">
                {/* Animated Success Icon */}
                <div className="relative w-24 h-24 mx-auto mb-6">
                  <div className="absolute inset-0 rounded-full bg-green-200 animate-ping" style={{ animationDuration: '2s' }} />
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center shadow-xl shadow-green-500/30 relative">
                    <i className="ri-check-line text-5xl text-white"></i>
                  </div>
                  {/* Orbiting dots */}
                  <div className="absolute inset-0 animate-spin" style={{ animationDuration: '8s' }}>
                    <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-orange-400 rounded-full" />
                  </div>
                  <div className="absolute inset-0 animate-spin" style={{ animationDuration: '12s', animationDirection: 'reverse' }}>
                    <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-green-300 rounded-full" />
                  </div>
                </div>

                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  Bem-vindo{nome ? `, ${nome.split(' ')[0]}` : ''}!
                </h2>
                <p className="text-gray-600 text-base mb-2">
                  Seu cadastro foi realizado com sucesso.
                </p>
                <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 rounded-full px-4 py-1.5 mb-8">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  <span className="text-sm font-medium text-green-700">Conta ativada e pronta para uso</span>
                </div>
              </div>

              {/* Card with user data */}
              <div className="relative z-10 bg-gradient-to-br from-orange-50 via-amber-50/50 to-orange-50 border border-orange-100 rounded-2xl p-6 mb-6">
                <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2 text-sm uppercase tracking-wide">
                  <i className="ri-user-line text-orange-500 w-5 h-5 flex items-center justify-center"></i>
                  Resumo do Cadastro
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center justify-between py-2 border-b border-orange-100/60">
                    <span className="text-gray-500 flex items-center gap-2">
                      <i className="ri-user-fill text-gray-400 w-4 h-4 flex items-center justify-center"></i>
                      Nome
                    </span>
                    <span className="font-medium text-gray-800 text-right">{nome}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-orange-100/60">
                    <span className="text-gray-500 flex items-center gap-2">
                      <i className="ri-id-card-line text-gray-400 w-4 h-4 flex items-center justify-center"></i>
                      Documento
                    </span>
                    <span className="font-medium text-gray-800 text-right">{documento}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-orange-100/60">
                    <span className="text-gray-500 flex items-center gap-2">
                      <i className="ri-mail-line text-gray-400 w-4 h-4 flex items-center justify-center"></i>
                      E-mail
                    </span>
                    <span className="font-medium text-gray-800 text-right">{email}</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-gray-500 flex items-center gap-2">
                      <i className="ri-smartphone-line text-gray-400 w-4 h-4 flex items-center justify-center"></i>
                      Celular
                    </span>
                    <span className="font-medium text-gray-800 text-right">{celular}</span>
                  </div>
                </div>
              </div>

              {/* Next steps */}
              <div className="relative z-10 bg-gray-50 rounded-xl p-5 mb-6">
                <h4 className="font-semibold text-gray-800 mb-3 text-sm flex items-center gap-2">
                  <i className="ri-flag-line text-orange-500 w-5 h-5 flex items-center justify-center"></i>
                  Próximos passos
                </h4>
                <ul className="space-y-3 text-sm text-gray-600">
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-green-600 text-xs w-4 h-4 flex items-center justify-center"></i>
                    </div>
                    <span>Complete seu perfil para ganhar <strong className="text-gray-800">pontos extras</strong></span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-compass-3-line text-orange-600 text-xs w-4 h-4 flex items-center justify-center"></i>
                    </div>
                    <span>Explore as <strong className="text-gray-800">campanhas disponíveis</strong> e comece a participar</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-gift-line text-orange-600 text-xs w-4 h-4 flex items-center justify-center"></i>
                    </div>
                    <span>Acumule pontos e <strong className="text-gray-800">resgate recompensas incríveis</strong></span>
                  </li>
                </ul>
              </div>

              {/* Countdown + Button */}
              <div className="relative z-10 space-y-4">
                <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                  <i className="ri-time-line text-orange-400 w-4 h-4 flex items-center justify-center"></i>
                  <span>Redirecionando para o login em</span>
                  <span className="inline-flex items-center justify-center w-8 h-8 bg-orange-100 rounded-lg font-bold text-orange-600 text-sm">
                    {redirectCountdown}
                  </span>
                  <span>segundos</span>
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/premiado/login')}
                  className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-4 px-6 rounded-xl font-semibold text-base hover:from-orange-600 hover:to-orange-700 hover:shadow-lg hover:shadow-orange-500/25 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  Acessar Minha Conta Agora
                  <i className="ri-arrow-right-line text-xl w-5 h-5 flex items-center justify-center"></i>
                </button>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-slate-200">
            <div className="flex flex-col items-center gap-4">
              {/* Logo */}
              <img 
                src="https://static.readdy.ai/image/22e92c18f0b031d861a5e53bf8e362fb/6356cf72c36f3ef424056fffdf6d6f31.png" 
                alt="GetPoints Logo" 
                className="h-8 w-auto"
              />
              
              {/* Copyright */}
              <p className="text-xs text-slate-500 text-center">
                © 2026 GetPoints Tecnologia, C.R.I.E. Ltda.
              </p>
              
              {/* Links */}
              <div className="flex items-center gap-4 text-xs">
                <a href="#" className="text-slate-600 hover:text-orange-600 transition-colors">
                  Termos de Uso
                </a>
                <span className="text-slate-300">•</span>
                <a href="#" className="text-slate-600 hover:text-orange-600 transition-colors">
                  Política de Privacidade
                </a>
                <span className="text-slate-300">•</span>
                <a href="#" className="text-slate-600 hover:text-orange-600 transition-colors">
                  Suporte
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Animation Overlay */}
      {showSuccessAnimation && (
        <RegistrationSuccessAnimation
          userName={nome}
          onComplete={() => {
            // Redirecionamento controlado pelo countdown da etapa 4
          }}
        />
      )}

      {/* Support Widget */}
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={() => {
            const modal = document.getElementById('chat-modal-cadastro');
            if (modal) modal.classList.remove('hidden');
          }}
          className="w-14 h-14 bg-orange-500 hover:bg-orange-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all flex items-center justify-center group"
        >
          <i className="ri-message-3-line text-2xl group-hover:scale-110 transition-transform"></i>
        </button>
      </div>

      {/* Chat Modal */}
      <div id="chat-modal-cadastro" className="hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center">
                <i className="ri-customer-service-2-line text-xl text-white"></i>
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Chat Online</h3>
                <p className="text-sm text-gray-500">Estamos aqui para ajudar</p>
              </div>
            </div>
            <button
              onClick={() => {
                const modal = document.getElementById('chat-modal-cadastro');
                if (modal) modal.classList.add('hidden');
              }}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
            >
              <i className="ri-close-line text-xl text-gray-500"></i>
            </button>
          </div>
          
          <div className="p-6 h-96 overflow-y-auto bg-gray-50">
            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-customer-service-2-line text-white text-sm"></i>
                </div>
                <div className="bg-white rounded-2xl rounded-tl-none p-4 shadow-sm max-w-[80%]">
                  <p className="text-sm text-gray-700">
                    Olá! Como posso ajudá-lo hoje?
                  </p>
                  <span className="text-xs text-gray-400 mt-1 block">Agora</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="p-4 border-t border-gray-200">
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Digite sua mensagem..."
                className="flex-1 px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
              />
              <button className="w-10 h-10 bg-orange-500 hover:bg-orange-600 text-white rounded-xl flex items-center justify-center transition-colors">
                <i className="ri-send-plane-fill text-lg"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Termos */}
      {mostrarTermos && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col overflow-hidden">
            <div className="p-6 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-2xl font-bold text-gray-800">Termos de Adesão</h2>
            </div>
            <div className="p-6 overflow-y-auto flex-1 text-sm text-gray-700 space-y-4">
              <p>
                <strong>1. Aceitação dos Termos</strong><br />
                Ao criar uma conta, você concorda com todos os termos e condições estabelecidos neste documento.
              </p>
              <p>
                <strong>2. Uso da Plataforma</strong><br />
                A plataforma destina-se ao gerenciamento de programas de incentivo e gamificação. O uso inadequado pode resultar no cancelamento da conta.
              </p>
              <p>
                <strong>3. Privacidade e Dados</strong><br />
                Seus dados pessoais serão tratados conforme nossa Política de Privacidade, em conformidade com a LGPD.
              </p>
              <p>
                <strong>4. Responsabilidades</strong><br />
                Você é responsável por manter a confidencialidade de suas credenciais de acesso e por todas as atividades realizadas em sua conta.
              </p>
              <p>
                <strong>5. Modificações</strong><br />
                Reservamo-nos o direito de modificar estes termos a qualquer momento, mediante notificação prévia.
              </p>
            </div>
            <div className="p-6 border-t border-gray-200 flex-shrink-0">
              <button
                onClick={() => setMostrarTermos(false)}
                className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 rounded-xl font-semibold hover:from-orange-600 hover:to-orange-700 transition-all whitespace-nowrap"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}